import { useAuth } from "@workos-inc/authkit-react";

// Same source App.tsx uses — AuthKit needs the org so it issues a session for
// THIS dashboard's organisation rather than a bare one.
const workosOrgId = import.meta.env.VITE_WORKOS_ORG_ID as string | undefined;

// Module-scoped so every widget on the page shares it: one expired session
// must produce ONE sign-in redirect, not one per in-flight request.
let reauthInFlight = false;

/**
 * Hook that returns an authed fetch — wraps window.fetch, injecting the
 * current WorkOS access token as `Authorization: Bearer <jwt>`.
 *
 * Usage:
 *   const api = useApi();
 *   const res = await api("/api/clients", { method: "GET" });
 */
export function useApi() {
  const { getAccessToken, signIn } = useAuth();

  return async function apiFetch(
    input: string,
    init: RequestInit = {},
  ): Promise<Response> {
    let token: string | null = null;
    try {
      token = await getAccessToken();
    } catch {
      // Not signed in (or token refresh failed) — fall through unauthenticated;
      // the 401 handler below re-prompts.
    }

    const headers = new Headers(init.headers ?? {});
    if (token) {
      headers.set("Authorization", `Bearer ${token}`);
    }
    if (init.body && !headers.has("Content-Type")) {
      headers.set("Content-Type", "application/json");
    }

    const res = await fetch(input, { ...init, headers });

    // ── AN EXPIRED SESSION MUST NOT LOOK LIKE A BROKEN DEPLOY ────────────────
    // Until 2026-07-29 NOTHING in src/ handled a 401. When a session expired,
    // every /api/* call returned 401, no caller checked, and the page sat on
    // "Loading…" forever with an empty console — indistinguishable from a
    // genuinely broken build. That has now cost us twice: Hank nearly reverted
    // a good commit (2026-07-27), and Atlas burned three fix cycles on a
    // dashboard that was never broken (2026-07-29). We had written the symptom
    // into Company Learnings but never removed the landmine. This removes it.
    //
    // Re-prompting is safe to fire from anywhere: AuthKit reuses an active
    // session, so a still-valid user is bounced straight back in. The `once`
    // guard stops a page full of parallel widgets triggering a signIn storm.
    if (res.status === 401 && !reauthInFlight) {
      reauthInFlight = true;
      try {
        signIn(workosOrgId ? { organizationId: workosOrgId } : undefined);
      } catch {
        // If the redirect itself fails, fall through and return the 401 so the
        // caller still sees a real error rather than a silent hang.
        reauthInFlight = false;
      }
    }

    return res;
  };
}
